<template>
  <div ref="containerRef" class="relative">
    <button
      class="p-2 rounded-full hover:bg-gray-100 transition-colors relative"
      aria-label="Notifications"
      @click="toggle"
    >
      <OverlayBadge
        v-if="hasUnread"
        :value="unreadCount > 99 ? '99+' : String(unreadCount)"
        severity="danger"
      >
        <i class="pi pi-bell text-xl text-gray-600" />
      </OverlayBadge>
      <i v-else class="pi pi-bell text-xl text-gray-600" />
    </button>

    <div
      v-if="isOpen"
      class="absolute right-0 top-full mt-1 w-80 max-h-96 flex flex-col bg-white rounded-lg border border-gray-200 shadow-lg z-50"
    >
      <div class="flex items-center justify-between px-3 py-2 border-b">
        <span class="font-semibold text-sm">Notifications</span>
        <div class="flex items-center gap-2">
          <button
            v-if="hasUnread"
            class="text-xs text-primary hover:underline"
            @click="handleMarkAllAsRead"
          >
            Mark all read
          </button>
          <button
            v-if="notifications.length > 0"
            class="text-xs text-gray-500 hover:text-gray-700 hover:underline"
            @click="handleClearAll"
          >
            Clear all
          </button>
        </div>
      </div>

      <div
        v-if="isLoading && notifications.length === 0"
        class="p-4 text-center"
      >
        <i class="pi pi-spin pi-spinner text-gray-400" />
      </div>

      <div
        v-else-if="notifications.length === 0"
        class="p-6 text-center text-sm text-gray-500"
      >
        No notifications
      </div>

      <div v-else class="overflow-y-auto">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          role="button"
          tabindex="0"
          class="px-3 py-2.5 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
          :class="{ 'bg-blue-50/50': !notification.is_read }"
          :aria-label="notificationAriaLabel(notification)"
          @click="handleNotificationClick(notification)"
          @keydown.enter.prevent="handleNotificationClick(notification)"
          @keydown.space.prevent="handleNotificationClick(notification)"
        >
          <div class="flex items-start gap-2">
            <i
              :class="levelIcon(notification.level)"
              class="mt-0.5 text-sm flex-shrink-0"
            />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1.5">
                <span
                  class="text-sm font-medium truncate"
                  :class="{ 'font-semibold': !notification.is_read }"
                >
                  {{ notification.title }}
                </span>
              </div>
              <p
                v-if="notification.message"
                class="text-xs text-gray-500 mt-0.5 line-clamp-2"
              >
                {{ notification.message }}
              </p>
              <div class="flex items-center gap-2 mt-1">
                <span class="text-xs text-gray-400">
                  {{ timeAgo(notification.created_at) }}
                </span>
                <button
                  v-if="notification.action_url && notification.action_label"
                  class="text-xs text-primary hover:underline"
                  @click.stop="handleActionClick(notification)"
                >
                  {{ notification.action_label }}
                </button>
              </div>
            </div>
            <div
              v-if="!notification.is_read"
              class="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { onClickOutside } from "@vueuse/core";
import { formatDistanceToNow } from "date-fns";
import OverlayBadge from "primevue/overlaybadge";
import { useAdminNotifications } from "@/composables/useAdminNotifications";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import type { AdminNotification } from "@/api/model";

const router = useRouter();
const { currentOrganizationId } = useCurrentOrganization();
const {
  notifications,
  unreadCount,
  isLoading,
  hasUnread,
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
  clearAll,
  startPolling,
  stopPolling,
} = useAdminNotifications();

const containerRef = ref<HTMLElement>();
const isOpen = ref(false);

onClickOutside(containerRef, () => {
  isOpen.value = false;
});

const toggle = async () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    const orgId = currentOrganizationId.value;
    if (orgId) {
      await fetchNotifications(orgId);
    }
  }
};

const handleNotificationClick = async (notification: AdminNotification) => {
  const orgId = currentOrganizationId.value;
  if (orgId && !notification.is_read) {
    await markAsRead(orgId, notification.id);
  }
  if (notification.action_url) {
    isOpen.value = false;
    router.push(notification.action_url);
  }
};

const handleActionClick = (notification: AdminNotification) => {
  if (notification.action_url) {
    isOpen.value = false;
    router.push(notification.action_url);
  }
};

const handleMarkAllAsRead = async () => {
  const orgId = currentOrganizationId.value;
  if (orgId) {
    await markAllAsRead(orgId);
  }
};

const handleClearAll = async () => {
  const orgId = currentOrganizationId.value;
  if (orgId) {
    await clearAll(orgId);
  }
};

const timeAgo = (dateString: string): string => {
  return formatDistanceToNow(new Date(dateString), { addSuffix: true });
};

const LEVEL_ICONS: Record<string, string> = {
  info: "pi pi-info-circle text-blue-500",
  success: "pi pi-check-circle text-green-500",
  warning: "pi pi-exclamation-triangle text-yellow-500",
  error: "pi pi-times-circle text-red-500",
};

const levelIcon = (level?: string): string => {
  return LEVEL_ICONS[level || "info"] ?? LEVEL_ICONS.info!;
};

const notificationAriaLabel = (notification: AdminNotification): string => {
  const readState = notification.is_read ? "Read" : "Unread";
  return `${readState} notification: ${notification.title}`;
};

watch(
  currentOrganizationId,
  (orgId) => {
    if (orgId) {
      fetchUnreadCount(orgId);
      startPolling(orgId);
    } else {
      stopPolling();
    }
  },
  { immediate: true }
);
</script>
